import { components } from "@/api-schema";
import { useOccupationSearchQuery } from "@/common/api/occupation/use-occupation-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["OccupationSearchBody"];

const FILTER_PAGE_SIZE = 50;

// Custom hook to fetch occupation data with pagination
function useOccupationFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: occupations,
    isPending: isLoading,
    error,
  } = useOccupationSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body,
    },
  });

  const options =
    occupations?.data
      .map((element) => ({
        id: element.uuid ?? "",
        name: element.name ?? "",
      }))
      .filter((option) => option.id && option.name)
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) || [];

  const loadedCount = occupations?.data?.length ?? 0;
  const total = occupations?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class OccupationFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "occupations";
  readonly labelSingular = "common:Occupation";
  readonly labelPlural = "ram:Occupations";
  readonly placeholder = "ram:SelectOccupations";
  readonly searchPlaceholder = "common:SearchOccupations";
  readonly emptyText = "casavi:NoOccupationsFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <OccupationFilterComponent filter={this} {...props} />;
  }
}

function OccupationFilterComponent({ filter, ...props }: { filter: OccupationFilter } & BaseFilterProps) {
  const { data, pagination } = useOccupationFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
